// APC brand tokens for the Motivated Skills tool. Palette pulled from the
// apc-ai-course repo (canonical APC brand): deep navy / orange / blue / green /
// gold / purple / slate, Fraunces (display) + Inter (body).
export const apc = {
  navy: '#16163f',
  navySoft: '#1e1e52',
  orange: '#f15a24',
  blue: '#2da3cb',
  green: '#10b981',
  gold: '#f39c12',
  purple: '#54548e',
  slate: '#8b8bc4',
  ink: '#1e293b',
  paper: '#f7f7f9',
  card: '#ffffff',
  line: '#e2e2e8',
  muted: '#64748b',
  display: "'Fraunces', Georgia, 'Times New Roman', serif",
  // S5a.8: Instrument Sans for body and controls, as the portal reads (PRD §9.1).
  body: "'Instrument Sans', 'Segoe UI', system-ui, -apple-system, sans-serif",
  // S5a.8: the design contract's buttons, matched to the portal's Daybreak
  // tokens rather than new colours: the client primary (--sl-act, navy, with
  // its edge and text) and the hairline secondary (--sl-rule, --sl-ink-2).
  // One radius (--sl-radius-md); no pill, no coloured edge on a rounded shape.
  act: '#3d5184',
  actEdge: '#2f406c',
  actInk: '#ffffff',
  rule: '#d4d3df',
  ink2: '#3d3d5c',
  radius: 8,
};

/** S5a.8: the contract's primary button, for a flow's start. */
export const primaryButton = {
  fontFamily: apc.body,
  fontSize: 17,
  fontWeight: 600,
  color: apc.actInk,
  background: apc.act,
  border: `1px solid ${apc.actEdge}`,
  borderRadius: apc.radius,
  padding: '13px 28px',
  cursor: 'pointer',
};

/** S5a.8: the contract's secondary button, a hairline. */
export const secondaryButton = {
  fontFamily: apc.body,
  fontSize: 16,
  fontWeight: 600,
  color: apc.ink2,
  background: 'transparent',
  border: `1px solid ${apc.rule}`,
  borderRadius: apc.radius,
  padding: '12px 24px',
  cursor: 'pointer',
  textDecoration: 'none',
};

// The four result zones (Knowdell vocabulary; warm APC labels). `key` matches
// the scoring model and the placement board quadrants.
export const ZONES = [
  {
    key: 'motivated',
    label: 'Motivated Skills',
    blurb: 'Love it + strong at it',
    how: 'You are good at this and you would happily do it all day. This is the gold. Aim your next chapter here.',
    color: apc.orange,
    tint: '#fdeee7',
    icon: 'brand_spark',
  },
  {
    key: 'burnout',
    label: 'Draining Strengths',
    blurb: "Strong at it + doesn't light you up",
    how: 'You are strong at this, so people lean on you for it, but it wears you down over time. Watch how much of your week it eats.',
    color: apc.purple,
    tint: '#ecebf4',
    icon: 'ui_alert',
  },
  {
    key: 'developmental',
    label: 'Growth Edges',
    blurb: 'Love it + still building',
    how: 'You love this but you are still building the muscle. A good place to invest on purpose.',
    color: apc.green,
    tint: '#e7f6ef',
    icon: 'ui_sprout',
  },
  {
    key: 'park',
    label: 'Park It',
    blurb: 'Not a strength, not a joy',
    how: 'Not a strength and not a joy. Fine to hand off or set down.',
    color: apc.slate,
    tint: '#f1f0f7',
    icon: 'ui_pause',
  },
];

export const ZONE_BY_KEY = Object.fromEntries(ZONES.map((z) => [z.key, z]));

// The 6 skill categories, color-coded so a client can read the sidebar at a
// glance. Muted, elegant tones that sit with APC navy/orange, not primary pops.
// Chosen so no category color matches a quadrant color (quadrants use orange /
// purple / green / slate). Spread across the APC family for distinctness.
export const CATEGORY_COLORS = {
  communication: apc.blue,  // #2da3cb
  interpersonal: apc.gold,  // #f39c12
  leadership: '#6d4aa8',    // violet (distinct from draining purple)
  analytical: '#3f6f8f',    // steel blue
  creative: '#d1467a',      // rose pop
  handsOn: '#5f8a6a',       // sage (distinct from growth green)
};
export const categoryColor = (key) => CATEGORY_COLORS[key] || apc.muted;

// Light tint of a hex, for tinted-wash surfaces (the approved accent style).
export const withAlpha = (hex, a) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
